import { CheckEmail, CheckUserName } from '@/common/services/realTimeCheckAPI';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';
import { useRouter } from '@tanstack/react-router';
import { QUERY_KEYS } from '../../api/apiEndpoints';
import { API_ENDPOINTS as API_MANAGEMENT_ENDPOINTS } from '../../../management/api/apiEndpoints';
import { useTranslation } from 'react-i18next';

export default function UserForm() {
    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, unknown> })?.record;
    const { t, i18n } = useTranslation();

    const userid = record && record.id !== undefined && record.id !== null
        ? Number(record.id)
        : undefined;

    const name =
        state && (state as { medicalProviderName?: string }).medicalProviderName !== undefined
            ? String((state as { medicalProviderName?: number }).medicalProviderName)
            : undefined;

    const medicalProviderId =
        state && (state as { medicalProviderId?: number }).medicalProviderId !== undefined
            ? Number((state as { medicalProviderId?: number }).medicalProviderId)
            : undefined;

    const userFormSchema: DynamicFormSchema = {
        layout: 'vertical',
        showResetButton: true,
        sections: [
            {
                key: 'user-details',
                title: 'User Form (Medical Provider: ' + name + ')',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'Firstname',
                        label: 'First Name',
                        type: 'input',
                        required: true,
                        requiredMessage: 'Please enter a First Name.',
                        autocomplete: 'false',
                        pattern: 'AlphabetsWithSpace',
                        validation: [
                            { min: 3, message: t('First Name must be at least 3 characters long.') },
                            { max: 50, message: t('First Name cannot be more than 50 characters long.') },
                        ],
                        colSpan: 12,
                    },
                    {
                        name: 'LastName',
                        label: 'Last Name',
                        type: 'input',
                        required: true,
                        requiredMessage: t('Please enter a Last Name.'),
                        autocomplete: 'false',
                        pattern: 'AlphabetsWithSpace',
                        validation: [
                            { min: 3, message: t('Last Name must be at least 3 characters long.') },
                            { max: 50, message: t('Last Name cannot be more than 50 characters long.') },
                        ],
                        colSpan: 12,
                    },
                    {
                        name: 'cellNo',
                        label: 'Mobile Number',
                        type: 'input',
                        required: true,
                        colSpan: 12,
                        pattern: 'PhoneNumber',
                    },
                    {
                        name: 'userName',
                        label: 'Username',
                        type: 'input',
                        readOnlyWhen: [{ field: 'id', operator: '>', value: '0' }],
                        required: true,
                        requiredMessage: t('Please enter a username.'),
                        autocomplete: 'false',
                        pattern: 'Alphabets',
                        validation: [
                            { min: 3, message: t('Name must be at least 3 characters long.') },
                            { max: 50, message: t('Name cannot be more than 50 characters long.') },
                            {
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 3) return Promise.resolve();

                                    try {
                                        const result = await CheckUserName(value, userid);
                                        if (result.exists) {
                                            return Promise.reject('UserName already exists.');
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        return Promise.reject(error.message || t('Error validating UserName.'));
                                    }
                                },
                            },
                        ],
                        hideOnEdit: false,
                        colSpan: 12,
                    },
                    {
                        name: 'email',
                        label: 'Email',
                        type: 'input',
                        required: true,
                        requiredMessage: t('An email is required to create a user.'),
                        pattern: 'Email',
                        validation: [
                            { max: 256, message: t('Email cannot be more than 256 characters long.') },
                            {
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 1) return Promise.resolve();

                                    try {
                                        const result = await CheckEmail(value, userid);
                                        if (result.exists) {
                                            return Promise.reject(t('Email already exists.'));
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        return Promise.reject(error.message || t('Error validating Email.'));
                                    }
                                },
                            },
                        ],
                        autocomplete: 'false',
                        colSpan: 12,
                    },
                    {
                        name: 'previousEmail',
                        label: 'Email',
                        type: 'input',
                        autocomplete: 'false',
                        colSpan: 12,
                        hidden: true,
                    },
                    {
                        name: 'isActive',
                        label: 'Status',
                        hidden: true,
                        type: 'switch',
                        colSpan: 12,
                    },
                    // 👇 Hidden fixed values
                    {
                        name: 'userType',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: `medical`,
                    },
                    {
                        name: 'roles',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: [3], // must be array of numbers
                    },
                    {
                        name: 'userTypeId',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: medicalProviderId,
                    },
                    // ✅ Added hidden medicalProviderId field
                    {
                        name: 'medicalProviderId',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: medicalProviderId,
                    },
                ],
            },
        ],
    };

    return (
        <DynamicForm
            schema={userFormSchema}
            submitApiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.SAVE_USER}
            getByIdApiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.GET_USER_BY_ID}
            updateApiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.UPDATE_USER}
            refreshTableDataKey={QUERY_KEYS.PROVIDERS.GET_MEDICAL_PROVIDERS_DETAIL}
            backUrl="history"
        />
    );
}